import { useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useControlcenterAPIInterface } from "@dealer-center/ddc-pages-controlcenter-fetch";
import { Box } from "@dealer-center/core-components";
import { MessagePage } from "@dealer-center/core-custom-components";

/**
 * Guards every `/cc/*` route against strictly-internal users.
 *
 * The keep/redirect decision is made *before* rendering `<Outlet />` on
 * purpose: mounting the outlet even briefly mounts `PagesControlcenterApp` →
 * `ControlCenterContainer` → `insertCc8Header()`, which injects Bootstrap
 * `cc8-theme.css` that never gets cleaned up and corrupts downstream
 * Interstate pages. So while `ccUserInfo` is pending we hold a loading render
 * and never fall through to `<Outlet />`.
 *
 * A user is "strictly internal" when they have an `internalUserId` and are NOT
 * account-switching (`/as/` in the path). Those users are redirected to
 * `/landing`. AS-ing users, external users, and error states pass through.
 *
 * The decision is intentionally stateless (no module-/ref-level memoization) so
 * a strictly-internal user who navigates back into `/cc/*` within the same
 * session is re-evaluated and redirected again.
 */
export const StrictlyInternalGuard = () => {
  const navigate = useNavigate();

  const { pathname } = useLocation();
  const isAsing = pathname.includes("/as/");
  const segments = pathname.split("/");
  const asIndex = segments.indexOf("as");
  const asAccount = asIndex >= 0 ? segments[asIndex + 1] : undefined;
  const asUser = asIndex >= 0 ? segments[asIndex + 2] : undefined;

  const { data: ccUserInfo, isError } = useControlcenterAPIInterface({
    asAccount,
    asUser
  });

  const isStrictlyInternal = !!ccUserInfo?.internalUserId && !isAsing;

  useEffect(() => {
    if (isStrictlyInternal) {
      navigate("/landing");
    }
  }, [isStrictlyInternal, navigate]);

  if (!ccUserInfo && !isError) {
    return (
      <Box data-testid="ddc-page">
        <MessagePage
          actionName="Loading user permissions"
          message="Loading user permissions"
        />
      </Box>
    );
  }

  if (isStrictlyInternal) {
    return null;
  }

  return <Outlet />;
};
